import { QueryClient } from '@tanstack/react-query';
import {
  createLegacyAuthGatewayClient,
  createPentaApiClient,
} from '@penta/penta-api';
import { getStoredToken } from '../contexts/AuthContext';
import { Middleware } from 'openapi-fetch';

export const createAuthMiddleware = (
  onUnauthorized?: () => void,
  getLanguage?: () => string | null,
): Middleware => ({
  onRequest: ({ request }) => {
    // Clone the request to safely mutate headers
    const newRequest = request.clone();
    const lang = getLanguage?.() || 'en';
    if (lang) {
      newRequest.headers.set('Accept-Language', lang);
    }

    // /v1/token is intentionally unauthenticated: backend should return 401 challenge
    if (newRequest.url.includes('/v1/token')) {
      return newRequest;
    }

    const token = getStoredToken();
    if (token) {
      newRequest.headers.set('Authorization', `Bearer ${token}`);
    }
    return newRequest;
  },
  onResponse: async ({ response, request }) => {
    // If we get 401, token is invalid or expired - auto logout
    // Výjimka: /v1/token endpoint vrací 401 schválně pro získání redirect URL, takže ho ignorujeme
    if (
      response.status === 401 &&
      onUnauthorized &&
      !request.url.includes('/v1/token')
    ) {
      console.warn(
        '[Auth] Token validation failed (401 Unauthorized) - logging out',
      );
      onUnauthorized();
    }
    return response;
  },
});

const getEnvVar = (
  key: 'VITE_API_URL' | 'VITE_LEGACY_AUTH_API_URL',
  placeholder: string,
) => {
  const val = window.__ENV__?.[key];
  if (val && val !== placeholder) {
    return val;
  }
  return import.meta.env[key];
};

export const setupLegacyAuthClient = (
  queryClient: QueryClient,
  onUnauthorized?: () => void,
  getLanguage?: () => string | null,
) => {
  return createLegacyAuthGatewayClient({
    queryClient,
    baseUrl:
      getEnvVar(
        'VITE_LEGACY_AUTH_API_URL',
        '__LEGACY_AUTH_API_URL_PLACEHOLDER__',
      ) || 'https://ma-dev.privatbanka.sk/api', // Runtime env, Build env, or default
    middlewares: [createAuthMiddleware(onUnauthorized, getLanguage)],
  });
};

export const setupPentaApiClient = (
  queryClient: QueryClient,
  onUnauthorized?: () => void,
  getLanguage?: () => string | null,
) => {
  return createPentaApiClient({
    queryClient,
    baseUrl:
      getEnvVar('VITE_API_URL', '__API_URL_PLACEHOLDER__') ||
      'https://ma-dev.privatbanka.sk/api',
    middlewares: [createAuthMiddleware(onUnauthorized, getLanguage)],
  });
};
